import React from 'react';
import { Link } from '@tanstack/react-router';
import { FiMapPin, FiClock, FiBriefcase, FiChevronRight } from 'react-icons/fi';
import type { ApplicantItem } from '../types';
import { Avatar } from '../../../ui/Avatar';
import { StatusTip } from '../../../ui/Tooltip';
import { applicantStatusTips } from '../../../../onboarding/copy';

interface ApplicantCardProps {
  applicant: ApplicantItem;
  isSelected: boolean;
  onSelect: () => void;
}

const statusBadgeStyles: Record<string, string> = {
  pending: 'bg-[#3b2a1a] text-[#FFA842]',
  accepted: 'bg-[#12381f] text-[#43E46D]',
  rejected: 'bg-[#3d1a1a] text-[#FF6B6B]',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
};

export const ApplicantCard: React.FC<ApplicantCardProps> = ({
  applicant,
  isSelected,
  onSelect,
}) => {
  return (
    <article
      onClick={onSelect}
      className={`cursor-pointer rounded-2xl border bg-[radial-gradient(circle_at_top,#181c24_0%,#090a0e_52%,#060607_100%)] p-4 transition-all duration-200 ${
        isSelected
          ? 'border-[#1784FF] shadow-[0_0_15px_rgba(23,132,255,0.25)]'
          : 'border-white/10 hover:border-white/25'
      }`}
    >
      {/* Top row: Avatar + Name + Roles + Location + Time + Badge */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {applicant.username ? (
            <Link
              to="/profile/$identifier"
              params={{ identifier: applicant.username }}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0"
            >
              <Avatar
                src={applicant.profileImage || undefined}
                name={applicant.fullName || applicant.username}
                size="lg"
                className="border border-white/15 hover:border-[#1784FF]/60 transition-colors"
              />
            </Link>
          ) : (
            <Avatar
              src={applicant.profileImage || undefined}
              name={applicant.fullName || applicant.username}
              size="lg"
              className="border border-white/15 shrink-0"
            />
          )}
          <div className="min-w-0 flex-1">
            {applicant.username ? (
              <Link
                to="/profile/$identifier"
                params={{ identifier: applicant.username }}
                onClick={(e) => e.stopPropagation()}
                className="inline-block max-w-full"
              >
                <h3 className="text-base font-semibold leading-tight text-white truncate hover:underline">
                  {applicant.username}
                </h3>
              </Link>
            ) : (
              <h3 className="text-base font-semibold leading-tight text-white truncate">
                {applicant.username}
              </h3>
            )}

            {/* Role badges */}
            {applicant.userTypes && applicant.userTypes.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {applicant.userTypes.map((role, idx) => (
                  <span
                    key={idx}
                    className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] font-medium text-zinc-300 truncate"
                  >
                    {role}
                  </span>
                ))}
              </div>
            )}

            {/* Location & Time */}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-400">
              <span className="inline-flex items-center gap-1 min-w-0">
                <FiMapPin size={13} className="text-[#1784FF] shrink-0" />
                <span className="truncate">{applicant.location || 'Location unspecified'}</span>
              </span>
              <span className="inline-flex items-center gap-1 shrink-0">
                <FiClock size={13} className="text-[#1784FF]" />
                {applicant.timeAgo}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <StatusTip content={applicantStatusTips[applicant.status]}>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                statusBadgeStyles[applicant.status] || 'bg-zinc-800 text-zinc-300'
              }`}
            >
              {statusLabels[applicant.status] || applicant.status}
            </span>
          </StatusTip>
          <FiChevronRight size={18} className="text-zinc-500" />
        </div>
      </div>

      {/* Sub-card: Applied to */}
      <div className="flex items-center gap-3 rounded-2xl border border-white/5 bg-black/40 p-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-zinc-800 text-zinc-300 shrink-0">
          <FiBriefcase size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase text-zinc-500">Applied to</p>
          <p className="truncate text-sm font-medium text-white">{applicant.appliedToGig}</p>
        </div>
      </div>
    </article>
  );
};
